import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useModuleAccess } from '../hooks/useModuleAccess';

/**
 * Help Center
 *
 * Embeds the static help portal (/help-portal/index.html) in an iframe and
 * passes the current tenant's enabled-module slugs to it via postMessage.
 * The portal's script.js applies a visibility filter to nav items and content
 * sections so the user only sees help for modules their tenant has enabled.
 *
 * Handshake:
 *   1. Iframe loads → script.js posts { type: 'oim/helpPortalReady' }
 *   2. We respond with { type: 'oim/setEnabledModules', modules: [...] }
 *   3. Portal applies the filter; refilters whenever modules change.
 *
 * Also forwards URL hashes (e.g. /help#oim) into the iframe for deep linking.
 */
const HelpCenter = () => {
  const location = useLocation();
  const iframeRef = useRef(null);
  const { isModuleEnabled } = useModuleAccess();

  // The full set of module slugs we know about, mapped against the tenant's
  // enablement state. Keep this in sync with the data-module attributes in
  // help-portal/index.html.
  // Mirroring the slugs gives us a stable, dependency-free contract with the
  // portal — easier to reason about than passing the entire moduleAccess
  // structure across the iframe boundary.
  const KNOWN_MODULE_SLUGS = [
    'safety', 'hira', 'capa', 'ptw', 'audits', 'training', 'documents', 'meetings', 'safety_assistant',
    'energy', 'ers', 'energy_data', 'emissions_data',
    'sustainability', 'ssum', 'cbam', 'gri', 'brsr', 'lca',
    'oim',
    'admin',
    'board',
  ];

  const computeEnabled = () =>
    KNOWN_MODULE_SLUGS.filter(slug => isModuleEnabled(slug));

  // Send module list to the portal whenever the iframe announces ready
  // OR whenever the enabled set changes.
  useEffect(() => {
    const sendModules = () => {
      const enabled = computeEnabled();
      const win = iframeRef.current && iframeRef.current.contentWindow;
      if (win) {
        win.postMessage({ type: 'oim/setEnabledModules', modules: enabled }, '*');
      }
    };

    const handleMessage = (event) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.data?.type === 'oim/helpPortalReady') {
        sendModules();
      }
    };

    window.addEventListener('message', handleMessage);

    // Also push proactively in case the portal already loaded (e.g. cached)
    // and we missed the ready signal — small delay lets the iframe finish
    // wiring its listener.
    const t = setTimeout(sendModules, 500);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(t);
    };
  }, [isModuleEnabled]);

  // Forward hash-based deep linking (e.g. /help#oim).
  useEffect(() => {
    const hash = location.hash;
    if (hash && iframeRef.current) {
      const section = hash.substring(1);
      iframeRef.current.src = `/help-portal/index.html#${section}`;
    }
  }, [location.hash]);

  return (
    <div
      className="help-center-container"
      style={{ width: '100%', height: '100vh', overflow: 'hidden', margin: 0, padding: 0 }}
    >
      <iframe
        ref={iframeRef}
        src="/help-portal/index.html"
        title="Ergoniq Help Center"
        style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        allow="fullscreen"
        loading="lazy"
      />
    </div>
  );
};

export default HelpCenter;
